import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Eye, EyeOff, Users, X, Star, Trash, Search, MoreVertical, MapPin, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { lookupGst, isValidGstin } from '../services/gstApi';
import { useCanWrite } from '../components/Can';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyScope } from '../hooks/useCompanyScope';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

interface ClientAddress {
  id: number;
  type: 'shipping' | 'billing';
  address: string;
  isDefault: boolean;
  isActive: boolean;
}

interface Client {
  id: number;
  name: string;
  contact: string | null;
  type: string | null;
  tenantId: number | null;
  addresses: ClientAddress[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ClientForm {
  name: string;
  contact: string;
  type: string;
  gstNo: string;
  isActive: boolean;
  isBuyer: boolean;
}

const ActionMenu: React.FC<{
  client: Client;
  onEdit: (client: Client) => void;
  onManageAddresses: (client: Client) => void;
  onDelete: (client: Client) => void;
}> = ({ client, onEdit, onManageAddresses, onDelete }) => {
  const canWrite = useCanWrite('clients');
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 160 });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && !buttonRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-lg text-[rgba(14,19,27,0.45)] hover:text-[#0E131B] hover:bg-[#F8F9FC] transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 99999 }}
          className="w-40 bg-white rounded-xl border border-[#E1E6EF] shadow-lg py-1"
        >
          <button
            onClick={() => { onEdit(client); setOpen(false); }}
            disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="w-full text-left px-3 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => { onManageAddresses(client); setOpen(false); }}
            disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="w-full text-left px-3 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            Manage Addresses
          </button>
          <button
            onClick={() => { onDelete(client); setOpen(false); }}
            disabled={!canWrite}
            title={!canWrite ? 'You have read-only access' : undefined}
            className="w-full text-left px-3 py-2 text-sm text-[#E02D3C] hover:bg-[#FEF2F2] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
          >
            Delete
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

// Distinct pill colour per client type (falls back to neutral grey for unknown/other types).
const typeStyle = (type?: string | null): string => {
  switch ((type || '').trim().toLowerCase()) {
    case 'supplier':
      return 'bg-[#FEF3C7] text-[#B45309] border border-[#FCD579]';         // amber
    case 'yarn purchased client':
      return 'bg-[#D1FAE5] text-[#047857] border border-[#A7F3D0]';         // emerald
    case 'product delivery client':
      return 'bg-[#EDE9FE] text-[#6D28D9] border border-[#DDD6FE]';         // violet
    case 'distributor':
      return 'bg-[#CCFBF1] text-[#0F766E] border border-[#99F6E4]';         // teal
    case 'retailer':
      return 'bg-[#FCE7F3] text-[#BE185D] border border-[#FBCFE8]';         // rose
    default:
      return 'bg-[#F1F3F9] text-[rgba(14,19,27,0.7)] border border-[#E1E6EF]'; // neutral
  }
};

const Clients: React.FC = () => {
  const canWrite = useCanWrite('clients');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [addresses, setAddresses] = useState<ClientAddress[]>([]);
  const [newAddress, setNewAddress] = useState<{ type: 'shipping' | 'billing'; address: string; isDefault: boolean }>({
    type: 'shipping',
    address: '',
    isDefault: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { showCompanyColumn } = useCompanyScope();
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ClientForm>();
  const gstValue = watch('gstNo');

  // GST auto-fill: look up the entered GSTIN and pre-fill name (+ address suggestion).
  const [gstLoading, setGstLoading] = useState(false);
  const handleFetchGst = async () => {
    const gstin = (getValues('gstNo') || '').trim();
    if (!gstin) { toast.error('Enter a GST number first'); return; }
    setGstLoading(true);
    try {
      const d = await lookupGst(gstin);
      if (d.name) setValue('name', d.name, { shouldDirty: true });
      setValue('gstNo', d.gstin, { shouldDirty: true });
      if (d.address) setNewAddress((p) => ({ ...p, address: d.address }));
      toast.success(`Found: ${d.name || d.legalName}${d.status ? ` (${d.status})` : ''}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'GST lookup failed');
    } finally {
      setGstLoading(false);
    }
  };

  // Fetch clients
  const { data: clientsResponse, isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: () => apiService.get('/clients'),
  });

  const clients = clientsResponse?.clients || [];

  // Filter clients by search
  const filteredClients = clients.filter((client: Client) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      client.name.toLowerCase().includes(q) ||
      (client.contact && client.contact.toLowerCase().includes(q)) ||
      (client.type && client.type.toLowerCase().includes(q)) ||
      ((client as any).gstNo && (client as any).gstNo.toLowerCase().includes(q))
    );
  });

  // Pin buyer parties to the top (they're the most-used in Challan / Bill). Array.sort is stable,
  // so non-buyers keep the backend's alphabetical order and buyers stay alphabetical among themselves.
  const sortedClients = [...filteredClients].sort(
    (a, b) => Number(!!(b as any).isBuyer) - Number(!!(a as any).isBuyer)
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedClients.length / pageSize));
  const paginatedClients = sortedClients.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  // Show group dividers ("Buyer parties" / "Other clients") only when this page actually has buyers.
  const pageHasBuyers = paginatedClients.some((c: any) => c.isBuyer);

  // ── Duplicate merge ──────────────────────────────────────────────────────
  const [showDedup, setShowDedup] = useState(false);
  const [keepChoice, setKeepChoice] = useState<Record<number, number>>({});   // groupIndex → keeper id
  const [excluded, setExcluded] = useState<Set<number>>(new Set());           // client ids to leave out of a merge
  const { data: dupResponse } = useQuery({
    queryKey: ['clients', 'duplicates'],
    queryFn: () => apiService.get('/clients/duplicates'),
  });
  const dupGroups: Client[][] = dupResponse?.groups || [];
  // Sensible default keeper: prefer a flagged buyer, then one that already has a GST, else the first.
  const defaultKeeper = (group: Client[]): number =>
    (group.find((c) => (c as any).isBuyer) || group.find((c) => (c as any).gstNo) || group[0]).id;

  const mergeMutation = useMutation({
    mutationFn: (payload: { keepId: number; mergeIds: number[] }) => apiService.post('/clients/merge', payload),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(res?.message || 'Merged');
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to merge clients'),
  });

  const mergeGroup = (groupIndex: number, group: Client[]) => {
    const keepId = keepChoice[groupIndex] ?? defaultKeeper(group);
    const mergeIds = group.filter((c) => c.id !== keepId && !excluded.has(c.id)).map((c) => c.id);
    if (!mergeIds.length) { toast.error('Select at least one duplicate to merge'); return; }
    mergeMutation.mutate({ keepId, mergeIds });
  };

  // Create client mutation
  const createMutation = useMutation({
    mutationFn: (data: ClientForm) => apiService.post('/clients', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client created successfully');
      setIsModalOpen(false);
      setSelectedTenantId(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create client');
    },
  });

  // Update client mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ClientForm }) =>
      apiService.put(`/clients/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client updated successfully');
      setIsModalOpen(false);
      setEditingClient(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update client');
    },
  });

  // Delete client mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client deleted successfully');
      setDeletingClient(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete client');
    },
  });

  // Deactivate client mutation
  const deactivateMutation = useMutation({
    mutationFn: (id: number) => apiService.put(`/clients/${id}/deactivate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client deactivated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate client');
    },
  });

  // Create address mutation (only used in onSubmit for existing clients)
  const createAddressMutation = useMutation({
    mutationFn: ({ clientId, data }: { clientId: number; data: any }) =>
      apiService.post(`/client-addresses/client/${clientId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add address');
    },
  });

  // Delete address mutation (only used in onSubmit for existing clients)
  const deleteAddressMutation = useMutation({
    mutationFn: (id: number) => apiService.delete(`/client-addresses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete address');
    },
  });

  // Update address mutation (only used in onSubmit for existing clients)
  const updateAddressMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiService.put(`/client-addresses/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update address');
    },
  });

  const onSubmit = async (data: ClientForm) => {
    if (editingClient) {
      // For existing clients, first update the client info
      await updateMutation.mutateAsync({ id: editingClient.id, data });

      // Get the original addresses from the client
      const originalAddresses = editingClient.addresses || [];
      const currentAddressIds = addresses.map(addr => addr.id);

      // Find addresses that were deleted (exist in original but not in current)
      const deletedAddresses = originalAddresses.filter(origAddr =>
        origAddr.id < 1000000 && !currentAddressIds.includes(origAddr.id)
      );

      // Delete removed addresses from backend
      for (const deletedAddr of deletedAddresses) {
        await deleteAddressMutation.mutateAsync(deletedAddr.id);
      }

      // Handle current addresses - add new ones and update existing ones
      for (const address of addresses) {
        if (address.id > 1000000) { // Temporary ID (new address)
          await createAddressMutation.mutateAsync({
            clientId: editingClient.id,
            data: {
              type: address.type,
              address: address.address,
              isDefault: address.isDefault,
              isActive: address.isActive
            }
          });
        } else {
          // Update existing address if needed
          const originalAddr = originalAddresses.find(orig => orig.id === address.id);
          if (originalAddr && (
            originalAddr.type !== address.type ||
            originalAddr.address !== address.address ||
            originalAddr.isDefault !== address.isDefault ||
            originalAddr.isActive !== address.isActive
          )) {
            await updateAddressMutation.mutateAsync({
              id: address.id,
              data: {
                type: address.type,
                address: address.address,
                isDefault: address.isDefault,
                isActive: address.isActive
              }
            });
          }
        }
      }
    } else {
      // For new clients, include addresses in the payload
      if (showCompanyColumn && !selectedTenantId) {
        toast.error('Please select a company');
        return;
      }
      const payload = {
        ...data,
        addresses: addresses.map(addr => ({
          type: addr.type,
          address: addr.address,
          isDefault: addr.isDefault,
          isActive: addr.isActive
        })),
        tenantId: showCompanyColumn ? selectedTenantId : undefined,
      };
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setAddresses(client.addresses || []);
    reset({
      name: client.name,
      contact: client.contact || '',
      type: client.type || '',
      gstNo: (client as any).gstNo || '',
      isActive: client.isActive,
      isBuyer: !!(client as any).isBuyer,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (client: Client) => {
    setDeletingClient(client);
  };

  const handleDeactivate = (client: Client) => {
    deactivateMutation.mutate(client.id);
  };

  const confirmDelete = () => {
    if (deletingClient) {
      deleteMutation.mutate(deletingClient.id);
    }
  };

  const openModal = () => {
    setEditingClient(null);
    setAddresses([]);
    setNewAddress({ type: 'shipping', address: '', isDefault: false });
    reset({ name: '', contact: '', type: '', gstNo: '', isActive: true, isBuyer: false });
    setSelectedTenantId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    setAddresses([]);
    setNewAddress({ type: 'shipping', address: '', isDefault: false });
    setSelectedTenantId(null);
    reset();
  };

  const handleAddAddress = () => {
    if (!newAddress.address.trim()) {
      toast.error('Please enter an address');
      return;
    }

    // Always add to local state (no immediate API calls)
    const tempAddress: ClientAddress = {
      id: Date.now(), // Temporary ID
      type: newAddress.type,
      address: newAddress.address,
      isDefault: newAddress.isDefault,
      isActive: true
    };
    setAddresses([...addresses, tempAddress]);

    setNewAddress({ type: 'shipping', address: '', isDefault: false });
  };

  const handleDeleteAddress = (addressId: number) => {
    // Always remove from local state (no immediate API calls)
    setAddresses(addresses.filter(addr => addr.id !== addressId));
  };

  const handleSetDefaultAddress = (addressId: number) => {
    // Always update local state (no immediate API calls)
    setAddresses(addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId
    })));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055a9]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load clients. Please try again.</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-2 py-4">
      {/* Single white card container */}
      <div className="bg-white rounded-xl border border-[#E8EBF1]">
        {/* Header bar */}
        <div className="flex items-center px-5 py-4 border-b border-[#E8EBF1]">
          <h1 className="text-2xl font-bold text-[#1B202A]">Clients</h1>
          <div className="ml-auto flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgba(14,19,27,0.4)]" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl pl-9 pr-3 py-2 text-sm text-[#0E131B] placeholder:text-[rgba(14,19,27,0.4)] focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] w-56"
              />
            </div>
            {/* Merge duplicates */}
            {canWrite && dupGroups.length > 0 && (
              <button
                onClick={() => setShowDedup(true)}
                className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl px-3 py-2 text-sm font-semibold hover:bg-amber-100 transition-colors"
                title="Review and merge duplicate clients"
              >
                Merge duplicates
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-600 text-white text-[11px]">{dupGroups.length}</span>
              </button>
            )}
            {/* Primary action */}
            <button
              onClick={openModal}
              disabled={!canWrite}
              title={!canWrite ? 'You have read-only access' : undefined}
              className="inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#0B2278] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Client
            </button>
          </div>
        </div>

        {/* Table */}
        {filteredClients.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="w-10 h-10 text-[rgba(14,19,27,0.2)] mx-auto mb-3" />
            <p className="text-sm text-[rgba(14,19,27,0.5)] font-medium mb-3">No clients found</p>
            <button onClick={openModal} className="bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#0B2278] transition-colors">
              Add Client
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="z-10 shadow-[0_1px_0_0_#E8EBF1]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider w-14 bg-[#F8F9FC]">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">GST</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Address</th>
                    {showCompanyColumn && (
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase tracking-wider bg-[#F8F9FC]">Company</th>
                    )}
                    <th className="px-4 py-3 w-12 bg-[#F8F9FC]"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((client: Client, idx: number) => {
                    const defaultAddr = client.addresses?.find(a => a.isDefault) || client.addresses?.[0];
                    const isBuyer = !!(client as any).isBuyer;
                    const prevBuyer = idx > 0 ? !!(paginatedClients[idx - 1] as any).isBuyer : null;
                    // Header when the buyer/non-buyer group changes — only if this page has buyers to separate.
                    const showGroupHeader = pageHasBuyers && (idx === 0 || isBuyer !== prevBuyer);
                    return (
                      <React.Fragment key={client.id}>
                      {showGroupHeader && (
                        <tr>
                          <td colSpan={showCompanyColumn ? 9 : 8} className={`px-4 py-2 text-[11px] font-bold uppercase tracking-wider border-b border-[#E8EBF1] ${isBuyer ? 'bg-[#EEF3FA] text-[#0055a9]' : 'bg-[#F8F9FC] text-[rgba(14,19,27,0.5)]'}`}>
                            {isBuyer ? 'Buyer parties' : 'Other clients'}
                          </td>
                        </tr>
                      )}
                      <motion.tr initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }} className="border-b border-[#F1F3F9] hover:bg-[#FAFBFD] transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">
                          {(currentPage - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-[#0E131B]">{client.name}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">{client.contact || <span className="text-[rgba(14,19,27,0.3)]">--</span>}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {(client as any).gstNo ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold font-mono uppercase bg-[#EEF2FF] text-[#4338CA] border border-[#C7D2FE]">{(client as any).gstNo}</span>
                          ) : (
                            <span className="text-[rgba(14,19,27,0.3)]">--</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1.5">
                            {(client as any).isBuyer && (
                              <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#EEF3FA] text-[#0055a9] border border-[#CFE0F2]">Buyer</span>
                            )}
                            {client.type && (
                              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${typeStyle(client.type)}`}>{client.type}</span>
                            )}
                            {!(client as any).isBuyer && !client.type && (
                              <span className="text-[rgba(14,19,27,0.3)]">--</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {client.isActive ? (
                            <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#D7F7C2] border border-[#A6EB84] text-[#006908]">Active</span>
                          ) : (
                            <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FCEDB9] border border-[#FCD579] text-[#A82C00]">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-[#0E131B] max-w-[220px] truncate">{defaultAddr?.address || <span className="text-[rgba(14,19,27,0.3)]">--</span>}</td>
                        {showCompanyColumn && (
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-[#0E131B]">
                            <CompanyCell tenantId={client.tenantId} />
                          </td>
                        )}
                        <td className="px-4 py-4 text-right">
                          <ActionMenu
                            client={client}
                            onEdit={handleEdit}
                            onManageAddresses={handleEdit}
                            onDelete={handleDelete}
                          />
                        </td>
                      </motion.tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8EBF1]">
                <div />
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-[#0055a9] text-white'
                          : 'text-[#0E131B] hover:bg-[#F8F9FC]'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-sm text-[rgba(14,19,27,0.65)]">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="bg-[#F8F9FC] border border-[#E1E6EF] rounded-lg px-2 py-1 text-sm text-[#0E131B] focus:outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Client Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center overflow-y-auto p-4">
          <div className="relative w-full max-w-2xl max-h-[92vh] flex flex-col rounded-xl border border-[#E1E6EF] shadow-2xl bg-white">
            {/* Modal header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#E8EBF1] shrink-0">
              <h3 className="text-lg font-bold text-[#1B202A]">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1 rounded-lg text-[rgba(14,19,27,0.45)] hover:text-[#0E131B] hover:bg-[#F8F9FC] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {showCompanyColumn && !editingClient && (
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-[#1B202A] uppercase tracking-wide">Company</h4>
                    <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
                  </div>
                )}
                {/* Client Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-bold text-[#1B202A] uppercase tracking-wide">Client Information</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Client Name *</label>
                      <input
                        {...register('name', { required: 'Client name is required' })}
                        className="block w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
                        placeholder="Enter client name"
                        autoFocus
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs text-[#E02D3C]">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Contact</label>
                      <input
                        {...register('contact')}
                        className="block w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
                        placeholder="Phone, email, etc."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0E131B] mb-1.5">GST No.</label>
                      <div className="flex gap-2">
                        <input
                          {...register('gstNo')}
                          onChange={(e) => setValue('gstNo', e.target.value.toUpperCase(), { shouldDirty: true })}
                          className="block w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] font-mono uppercase"
                          placeholder="24XXXXX0000X0ZX"
                          maxLength={15}
                        />
                        <button
                          type="button"
                          onClick={handleFetchGst}
                          disabled={gstLoading || !isValidGstin(gstValue || '')}
                          className="shrink-0 px-3 py-2 rounded-xl bg-[#0055a9] text-white text-sm font-medium hover:bg-[#00478f] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          title={isValidGstin(gstValue || '') ? 'Auto-fill name & address from this GST number' : 'Enter a valid 15-character GSTIN to enable'}
                        >
                          {gstLoading ? '…' : 'Fetch'}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0E131B] mb-1.5">Client Type</label>
                      <select
                        {...register('type')}
                        className="block w-full border border-[#E1E6EF] rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
                      >
                        <option value="">Select client type</option>
                        <option value="Yarn Purchased Client">Yarn Purchased Client</option>
                        <option value="Product Delivery Client">Product Delivery Client</option>
                        <option value="Supplier">Supplier</option>
                        <option value="Distributor">Distributor</option>
                        <option value="Retailer">Retailer</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center">
                      <input
                        {...register('isActive')}
                        type="checkbox"
                        className="h-4 w-4 text-[#0055a9] focus:ring-[#0055a9]/20 border-[#E1E6EF] rounded"
                      />
                      <label className="ml-2 block text-sm text-[#0E131B]">Active</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('isBuyer')}
                        type="checkbox"
                        className="h-4 w-4 text-[#0055a9] focus:ring-[#0055a9]/20 border-[#E1E6EF] rounded"
                      />
                      <label className="ml-2 block text-sm text-[#0E131B]">
                        Buyer party
                        <span className="ml-1 text-xs text-[rgba(14,19,27,0.45)]">(shown in Challan / Bill / Yarn buyer lists)</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Address Management */}
                <div className="space-y-4 pt-1 border-t border-[#EEF1F6]">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-[#1B202A] uppercase tracking-wide">Addresses</h4>
                    <span className="text-xs text-[rgba(14,19,27,0.45)]">Optional · add one or more</span>
                  </div>

                  {/* Added addresses list — shown first so it reads top-to-bottom */}
                  {addresses.length > 0 && (
                    <div className="space-y-2">
                      {addresses.map((address) => (
                        <div key={address.id} className="group bg-white border border-[#E1E6EF] rounded-xl p-3 flex items-start gap-3">
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#EEF3FA] text-[#0055a9]">
                            <MapPin className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-xs font-semibold uppercase tracking-wide text-[#0055a9] bg-[#EEF3FA] px-2 py-0.5 rounded-full">
                                {address.type}
                              </span>
                              {address.isDefault && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#B45309] bg-[#FEF3C7] px-2 py-0.5 rounded-full">
                                  <Star className="h-3 w-3 fill-[#F59E0B] text-[#F59E0B]" /> Default
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#0E131B] mt-1 leading-relaxed break-words">{address.address}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!address.isDefault && (
                              <button
                                type="button"
                                onClick={() => handleSetDefaultAddress(address.id)}
                                className="p-1.5 rounded-lg text-[rgba(14,19,27,0.35)] hover:text-[#F59E0B] hover:bg-[#F8F9FC] transition-colors"
                                title="Set as default"
                              >
                                <Star className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteAddress(address.id)}
                              className="p-1.5 rounded-lg text-[rgba(14,19,27,0.35)] hover:text-[#E02D3C] hover:bg-[#F8F9FC] transition-colors"
                              title="Delete address"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Address */}
                  <div className="bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      {/* Segmented type toggle */}
                      <div className="inline-flex rounded-lg border border-[#E1E6EF] bg-white p-0.5">
                        {(['shipping', 'billing'] as const).map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setNewAddress({ ...newAddress, type: t })}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize transition-colors ${
                              newAddress.type === t
                                ? 'bg-[#0055a9] text-white shadow-sm'
                                : 'text-[rgba(14,19,27,0.6)] hover:text-[#0E131B]'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                      <label className="flex items-center gap-2 text-sm text-[#0E131B] cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={newAddress.isDefault}
                          onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                          className="h-4 w-4 text-[#0055a9] focus:ring-[#0055a9]/20 border-[#E1E6EF] rounded"
                        />
                        Set as default
                      </label>
                    </div>
                    <textarea
                      value={newAddress.address}
                      onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                      className="block w-full border border-[#E1E6EF] rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] resize-none leading-relaxed"
                      placeholder="Shop / building, street, area, city, state, PIN"
                      rows={3}
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleAddAddress}
                        disabled={!newAddress.address.trim()}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0B2278] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Plus className="h-4 w-4" /> Add address
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#E8EBF1] bg-[#F8F9FC]/50 rounded-b-xl shrink-0">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-sm font-semibold text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0B2278] disabled:opacity-50 transition-colors"
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Duplicates Modal */}
      {showDedup && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E6EF]">
              <div>
                <h3 className="text-lg font-bold text-[#1B202A]">Merge duplicate clients</h3>
                <p className="text-xs text-[rgba(14,19,27,0.5)] mt-0.5">Pick the correct name to keep in each group. Uncheck any row that is actually a different client.</p>
              </div>
              <button onClick={() => setShowDedup(false)} className="text-[rgba(14,19,27,0.4)] hover:text-[#0E131B] text-xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {dupGroups.length === 0 ? (
                <div className="text-center text-[rgba(14,19,27,0.45)] py-10 text-sm">No duplicate groups found. 🎉</div>
              ) : dupGroups.map((group, gi) => {
                const keepId = keepChoice[gi] ?? defaultKeeper(group);
                const toMerge = group.filter((c) => c.id !== keepId && !excluded.has(c.id));
                return (
                  <div key={gi} className="border border-[#E1E6EF] rounded-xl overflow-hidden">
                    <div className="px-4 py-2 bg-[#F8F9FC] text-xs font-semibold text-[rgba(14,19,27,0.6)] uppercase tracking-wide">Group {gi + 1} · {group.length} entries</div>
                    <div className="divide-y divide-[#EEF0F6]">
                      {group.map((c) => {
                        const isKeeper = c.id === keepId;
                        const isExcluded = excluded.has(c.id);
                        return (
                          <div key={c.id} className={`flex items-center gap-3 px-4 py-2.5 ${isKeeper ? 'bg-blue-50/50' : ''}`}>
                            <label className="flex items-center gap-2 cursor-pointer shrink-0">
                              <input type="radio" name={`keep-${gi}`} checked={isKeeper}
                                onChange={() => setKeepChoice((p) => ({ ...p, [gi]: c.id }))} />
                              <span className="text-[11px] font-semibold text-[#0055a9] w-10">{isKeeper ? 'KEEP' : ''}</span>
                            </label>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-[#0E131B] truncate">
                                {c.name}
                                {(c as any).isBuyer && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">BUYER</span>}
                              </div>
                              {(c as any).gstNo && <div className="text-[11px] text-[rgba(14,19,27,0.45)] font-mono">{(c as any).gstNo}</div>}
                            </div>
                            {!isKeeper && (
                              <label className="flex items-center gap-1.5 text-xs text-[rgba(14,19,27,0.6)] cursor-pointer shrink-0">
                                <input type="checkbox" checked={!isExcluded}
                                  onChange={() => setExcluded((prev) => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })} />
                                merge
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[#F8F9FC] border-t border-[#EEF0F6]">
                      <span className="text-xs text-[rgba(14,19,27,0.5)]">
                        {toMerge.length ? `Merge ${toMerge.length} into "${group.find((c) => c.id === keepId)?.name}"` : 'Nothing selected to merge'}
                      </span>
                      <button
                        onClick={() => mergeGroup(gi, group)}
                        disabled={mergeMutation.isPending || !toMerge.length}
                        className="px-3 py-1.5 rounded-lg bg-[#0055a9] text-white text-sm font-medium hover:bg-[#00478f] disabled:opacity-50"
                      >
                        {mergeMutation.isPending ? 'Merging…' : 'Merge group'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-3 border-t border-[#E1E6EF] text-right">
              <button onClick={() => setShowDedup(false)} className="px-4 py-2 text-sm font-medium text-[#0E131B] bg-[#F2F2F7] rounded-xl hover:bg-[#EAEBF0]">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingClient && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center overflow-y-auto p-4"
          tabIndex={-1} ref={el => el?.focus()}
          onKeyDown={e => { if (e.key === 'Escape') setDeletingClient(null); if (e.key === 'Enter') { e.preventDefault(); confirmDelete(); } }}>
          <div className="relative w-full max-w-md rounded-xl border border-[#E1E6EF] shadow-2xl bg-white">
            {/* Modal header */}
            <div className="px-6 py-4 border-b border-[#E8EBF1]">
              <h3 className="text-lg font-bold text-[#1B202A]">Delete Client</h3>
            </div>

            {/* Modal body */}
            <div className="px-6 py-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[#FEF2F2] flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-[#E02D3C]" />
              </div>
              <h4 className="text-base font-semibold text-[#0E131B] mb-2">Are you sure?</h4>
              <p className="text-sm text-[rgba(14,19,27,0.65)]">
                You are about to delete <span className="font-semibold text-[#0E131B]">"{deletingClient.name}"</span>. This action cannot be undone.
              </p>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#E8EBF1] bg-[#F8F9FC]/50 rounded-b-xl">
              <button
                onClick={() => setDeletingClient(null)}
                className="px-4 py-2 text-sm font-semibold text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#E02D3C] rounded-xl hover:bg-[#C82333] disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </motion.div>
  );
};

export default Clients;
